import React from 'react';
import { FaDownload } from 'react-icons/fa';
import moment from 'moment';

/**
 * ChatMessage - Individual message bubble component
 *
 * Renders user and bot messages with different styling
 * Special formatting for welcome messages with sections and emojis
 */
const ChatMessage = ({ message }) => {
  const isUser = message.sender === 'user';
  const isWelcome = message.isWelcome;
  const isUpdate = message.isUpdate;
  const isDisclaimer = message.isDisclaimer;
  const handleExportMessage = () => {
    const timestamp = moment(message.timestamp).format('MMMM D, YYYY [at] h:mm A');
    const sender = isUser ? 'User' : 'Mike';
    const content = `${sender} — ${timestamp}\n\n${message.text}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `mike-message-${moment(message.timestamp).format('YYYY-MM-DD-HHmmss')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /**
   * Format welcome/update messages with sections and formatting
   */
  const formatSpecialMessage = (text) => {
    return text.split('\n').map((line, idx) => {
      // Headers (lines starting with emojis)
      if (line.match(/^[👋📊🔍🛠️📈💡🔴✅⚠️💰⏳🏆📝📍]/)) {
        return (
          <div
            key={idx}
            style={{
              fontWeight: line.includes(':') ? '600' : 'bold',
              fontSize: '15px',
              marginTop: idx > 0 ? '12px' : '0',
              marginBottom: '8px',
              color: '#333'
            }}
          >
            {line}
          </div>
        );
      }

      // Separator lines
      if (line.includes('━')) {
        return (
          <div
            key={idx}
            style={{
              borderBottom: '1px solid #ddd',
              margin: '8px 0'
            }}
          />
        );
      }

      // Bullet points and list items
      if (line.match(/^[•✅⚠️📍📝🔴💰⏳🏆]/) || line.trim().startsWith('•')) {
        return (
          <div
            key={idx}
            style={{
              paddingLeft: '8px',
              marginBottom: '4px',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
            {line}
          </div>
        );
      }

      // Empty lines (spacing)
      if (!line.trim()) {
        return <div key={idx} style={{ height: '8px' }} />;
      }

      // Regular lines
      return (
        <div
          key={idx}
          style={{
            marginBottom: '4px',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {line}
        </div>
      );
    });
  };

  /**
   * Format regular bot messages - preserve formatting like **bold**
   */
  const formatRegularMessage = (text) => {
    // Simple markdown-like formatting
    const lines = text.split('\n');
    return lines.map((line, idx) => {
      // Bold text **text**
      const withBold = line.split(/\*\*(.*?)\*\*/g).map((part, i) => {
        if (i % 2 === 1) {
          return <strong key={i}>{part}</strong>;
        }
        return part;
      });

      // Bullet points
      if (line.trim().startsWith('•') || line.trim().startsWith('-')) {
        return (
          <div key={idx} style={{ paddingLeft: '8px', marginBottom: '4px' }}>
            {withBold}
          </div>
        );
      }

      // Empty lines
      if (!line.trim()) {
        return <div key={idx} style={{ height: '6px' }} />;
      }

      // Regular lines
      return (
        <div key={idx} style={{ marginBottom: '4px' }}>
          {withBold}
        </div>
      );
    });
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        marginBottom: '12px'
      }}
    >
      <div
        style={{
          position: 'relative',
          backgroundColor: isUser
            ? '#0056b3'
            : isDisclaimer
            ? '#f8f9fa'
            : isWelcome
            ? '#e7f3ff'
            : isUpdate
            ? '#fff3cd'
            : '#f0f0f0',
          color: isUser ? 'white' : isDisclaimer ? '#6c757d' : '#333',
          borderRadius: isDisclaimer ? '12px' : isUser
            ? '18px 18px 4px 18px'
            : '18px 18px 18px 4px',
          padding: isDisclaimer ? '14px 18px' : isWelcome ? '16px 20px' : '12px 16px',
          maxWidth: isDisclaimer ? '95%' : isWelcome ? '90%' : '80%',
          fontSize: isDisclaimer ? '12px' : '14px',
          lineHeight: isDisclaimer ? '1.6' : '1.5',
          fontStyle: isDisclaimer ? 'italic' : 'normal',
          border: isDisclaimer
            ? '1px solid #dee2e6'
            : isWelcome
            ? '1px solid #b3d9ff'
            : isUpdate
            ? '1px solid #ffc107'
            : 'none',
          boxShadow: isDisclaimer
            ? '0 1px 3px rgba(0,0,0,0.08)'
            : isWelcome || isUpdate
            ? '0 2px 8px rgba(0,0,0,0.1)'
            : 'none',
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap'
        }}
      >
        {/* Disclaimer header */}
        {isDisclaimer && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '8px',
            paddingBottom: '8px',
            borderBottom: '1px solid #e9ecef'
          }}>
            <span style={{ marginRight: '8px', fontSize: '14px' }}>ℹ️</span>
            <span style={{
              fontSize: '11px',
              fontWeight: '600',
              color: '#495057',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontStyle: 'normal'
            }}>
              AI Disclaimer
            </span>
          </div>
        )}

        {/* Message content */}
        <div>
          {isDisclaimer
            ? message.text
            : isWelcome || isUpdate
            ? formatSpecialMessage(message.text)
            : isUser
            ? message.text
            : formatRegularMessage(message.text)}
          {message.isStreaming && (
            <span style={{
              display: 'inline-block',
              width: '2px',
              height: '14px',
              backgroundColor: '#333',
              marginLeft: '2px',
              verticalAlign: 'text-bottom',
              animation: 'mikeCursorBlink 1s step-end infinite'
            }} />
          )}
        </div>

        {/* Timestamp and export */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginTop: '6px'
          }}
        >
          {/* Export button */}
          {!isDisclaimer && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleExportMessage();
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '2px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: isUser ? 'rgba(255,255,255,0.85)' : '#666',
                fontSize: '10px',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = isUser ? '#fff' : '#0056b3';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = isUser ? 'rgba(255,255,255,0.85)' : '#666';
              }}
              title="Export this message"
            >
              <FaDownload size={10} />
              <span>Export</span>
            </button>
          )}
          {isDisclaimer && <div />}

          <div
            style={{
              fontSize: '11px',
              opacity: isUser ? 0.8 : 0.6,
              color: isUser ? 'white' : '#666'
            }}
          >
            {moment(message.timestamp).format('h:mm A')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
